import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import statisticsService, { PredefinedReport } from '../../services/statisticsService';

const PredefinedReports: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<PredefinedReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<PredefinedReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les rapports prédéfinis depuis le backend
  useEffect(() => {
    const loadReports = async () => {
      try {
        setLoadingReports(true);
        setError(null);
        const backendReports = await statisticsService.getPredefinedReports();
        setReports(backendReports);
      } catch (err: any) {
        console.error('Erreur lors du chargement des rapports:', err);
        setError(err.message || 'Erreur lors du chargement des rapports');
      } finally {
        setLoadingReports(false);
      }
    };

    loadReports();
  }, []);

  // Mapping des catégories backend vers les icônes et couleurs
  const getReportDisplayInfo = (category: string) => {
    const categoryMap: Record<string, { icon: string; color: string; displayName: string }> = {
      'quotidien': { icon: 'fas fa-calendar-day', color: 'blue', displayName: 'Quotidien' },
      'hebdomadaire': { icon: 'fas fa-chart-line', color: 'green', displayName: 'Hebdomadaire' },
      'mensuel': { icon: 'fas fa-chart-area', color: 'purple', displayName: 'Mensuel' },
      'analyse': { icon: 'fas fa-chart-bar', color: 'orange', displayName: 'Analyse' },
      'qualité': { icon: 'fas fa-shield-alt', color: 'yellow', displayName: 'Qualité' }
    };
    
    return categoryMap[category] || { icon: 'fas fa-file-alt', color: 'gray', displayName: category };
  };

  const generateReport = async (report: PredefinedReport) => {
    setLoading(true);
    setSelectedReport(report);
    
    try {
      // Générer le rapport via l'API en passant les données du rapport
      const reportData = await statisticsService.generatePredefinedReport(report.id, report);
      console.log('Rapport généré:', reportData);
      
      // Mettre à jour le rapport avec les données générées
      setSelectedReport({
        ...report,
        data: reportData
      });
      
    } catch (error) {
      console.error('Erreur lors de la génération du rapport:', error);
      setError('Erreur lors de la génération du rapport');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: 'pdf' | 'excel' | 'csv') => {
    if (!selectedReport || !selectedReport.data) {
      setError('Aucun rapport sélectionné ou données manquantes');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Préparer les données du rapport pour l'export
      const reportData = {
        title: selectedReport.name,
        description: selectedReport.description,
        data_type: selectedReport.data?.type || 'general',
        data: selectedReport.data.data || selectedReport.data
      };
      
      await statisticsService.exportReport(reportData, format);
      
    } catch (error: any) {
      console.error('Erreur lors de l\'export:', error);
      setError(`Erreur lors de l'export ${format.toUpperCase()}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  // Composant pour afficher les données de rapport de manière lisible
  const renderReportData = (data: any) => {
    if (!data) return null;

    console.log('Rendering report data:', data);

    // Si c'est un rapport général (données directes)
    if (data.total_pesées !== undefined || data.total_pesees !== undefined) {
      // Déterminer la période pour l'affichage
      const getPeriodInfo = () => {
        const now = new Date();
        if (data.period === 'day') {
          return `Aujourd'hui (${now.toLocaleDateString('fr-FR')})`;
        } else if (data.period === 'week') {
          const weekStart = new Date(now);
          weekStart.setDate(now.getDate() - 7);
          return `7 derniers jours (${weekStart.toLocaleDateString('fr-FR')} - ${now.toLocaleDateString('fr-FR')})`;
        } else if (data.period === 'month') {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          return `Ce mois (${monthStart.toLocaleDateString('fr-FR')} - ${now.toLocaleDateString('fr-FR')})`;
        }
        return 'Période non spécifiée';
      };

      return (
        <div className="space-y-4">
          {/* En-tête avec période */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <i className="fas fa-calendar-alt text-blue-600 mr-2"></i>
              <span className="text-blue-800 font-medium">{getPeriodInfo()}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-blue-600">{data.total_pesées || data.total_pesees || 0}</div>
              <div className="text-sm text-gray-600">Total Pesées</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-green-600">{data.pesées_complétées || data.pesees_completees || 0}</div>
              <div className="text-sm text-gray-600">Complétées</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-orange-600">{data.pesées_en_cours || data.pesees_en_cours || 0}</div>
              <div className="text-sm text-gray-600">En Cours</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-red-600">{data.pesées_refoulées || data.pesees_refoulees || 0}</div>
              <div className="text-sm text-gray-600">Refoulées</div>
            </div>
          </div>
          
          {data.poids_total && (
            <div className="bg-white p-4 rounded-lg border">
              <h5 className="font-medium text-gray-900 mb-2">Poids Total</h5>
              <div className="text-lg font-semibold text-gray-800">
                {parseFloat(data.poids_total).toLocaleString('fr-FR')} kg
              </div>
            </div>
          )}
        </div>
      );
    }

    // Si c'est un rapport par produit (tableau d'objets avec produit__nom)
    if (Array.isArray(data) && data.length > 0 && data[0].produit__nom !== undefined) {
      return (
        <div className="space-y-4">
          <h5 className="font-medium text-gray-900">Répartition par Produit</h5>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre de Pesées
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Poids Total (kg)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((product: any, index: number) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.produit__nom || 'Non spécifié'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parseFloat(product.total_weight || 0).toLocaleString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Si c'est un rapport par client (tableau d'objets avec client__nom)
    if (Array.isArray(data) && data.length > 0 && data[0].client__nom !== undefined) {
      return (
        <div className="space-y-4">
          <h5 className="font-medium text-gray-900">Répartition par Client</h5>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre de Pesées
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Poids Total (kg)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((client: any, index: number) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {client.client__nom || 'Non spécifié'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parseFloat(client.total_weight || 0).toLocaleString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Si c'est un rapport par qualité KOR (tableau d'objets avec quality_level)
    if (Array.isArray(data) && data.length > 0 && data[0].quality_level !== undefined) {
      return (
        <div className="space-y-4">
          <h5 className="font-medium text-gray-900">Répartition par Qualité KOR</h5>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Niveau de Qualité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre de Pesées
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Poids Total (kg)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score Moyen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((quality: any, index: number) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {quality.quality_level_display || quality.quality_level || 'Qualité inconnue'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {quality.count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parseFloat(quality.total_weight || 0).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parseFloat(quality.avg_quality_score || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Si c'est un rapport par humidité (tableau d'objets avec humidity_range)
    if (Array.isArray(data) && data.length > 0 && data[0].humidity_range !== undefined) {
      return (
        <div className="space-y-4">
          <h5 className="font-medium text-gray-900">Répartition par Taux d'Humidité</h5>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tranche d'Humidité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre de Pesées
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Poids Total (kg)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Humidité Moyenne (%)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((humidity: any, index: number) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {humidity.humidity_range || 'Tranche inconnue'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {humidity.count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parseFloat(humidity.total_weight || 0).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parseFloat(humidity.avg_humidity || 0).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Fallback pour d'autres types de données - affichage en tableau
    const renderDataAsTable = (data: any) => {
      // Si c'est un objet simple avec des propriétés numériques
      if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
        const entries = Object.entries(data).filter(([key, _value]) => 
          key !== 'type' && key !== 'filters' && key !== 'chart_type'
        );
        
        if (entries.length > 0) {
          return (
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Indicateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valeur
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {entries.map(([key, value], index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {typeof value === 'number' 
                          ? value.toLocaleString('fr-FR') 
                          : String(value)
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
      }
      
      // Si c'est un tableau d'objets
      if (Array.isArray(data) && data.length > 0) {
        const firstItem = data[0];
        if (typeof firstItem === 'object' && firstItem !== null) {
          const keys = Object.keys(firstItem);
          
          return (
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {keys.map((key) => (
                      <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {keys.map((key) => (
                        <td key={key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {typeof item[key] === 'number' 
                            ? item[key].toLocaleString('fr-FR') 
                            : String(item[key] || '-')
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
      }
      
      // Fallback final - JSON formaté mais plus compact
      return (
        <div className="bg-white p-4 rounded-lg border">
          <h5 className="font-medium text-gray-900 mb-2">Données du rapport</h5>
          <pre className="text-sm text-gray-600 bg-gray-50 p-3 rounded border overflow-auto max-h-64">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      );
    };

    return renderDataAsTable(data);
  };

  return (
    <div className="space-y-6">
      {/* Liste des rapports prédéfinis */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rapports Disponibles</h3>
        
        {loadingReports ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Chargement des rapports...</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <i className="fas fa-exclamation-triangle text-red-600 mr-2"></i>
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.map((report) => {
              const displayInfo = getReportDisplayInfo(report.category);
              return (
                <div
                  key={report.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${getColorClasses(displayInfo.color)}`}
                  onClick={() => generateReport(report)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <i className={`${displayInfo.icon} text-xl`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium">{report.name}</h4>
                      <p className="text-sm opacity-75 mt-1">{report.description}</p>
                      <div className="mt-2">
                        <span className="inline-block px-2 py-1 text-xs font-medium bg-white bg-opacity-50 rounded">
                          {displayInfo.displayName}
                        </span>
                      </div>
                      <p className="text-xs mt-2 opacity-75">
                        Créé: {format(new Date(report.created_at), 'dd/MM/yyyy', { locale: fr })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rapport sélectionné */}
      {selectedReport && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{selectedReport.name}</h3>
              <p className="text-sm text-gray-600">{selectedReport.description}</p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => exportReport('pdf')}
                className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
              >
                <i className="fas fa-file-pdf mr-1"></i>
                PDF
              </button>
              <button
                onClick={() => exportReport('excel')}
                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors"
              >
                <i className="fas fa-file-excel mr-1"></i>
                Excel
              </button>
              <button
                onClick={() => exportReport('csv')}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
              >
                <i className="fas fa-file-csv mr-1"></i>
                CSV
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Génération du rapport...</span>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4">
              {selectedReport.data ? (
                renderReportData(selectedReport.data.data || selectedReport.data)
              ) : (
                <div className="space-y-4">
                  <div className="text-center text-gray-500 mb-4">
                    <i className="fas fa-chart-bar text-3xl mb-2"></i>
                    <p>Contenu du rapport {selectedReport.name}</p>
                    <p className="text-sm">Cliquez sur "Générer le Rapport" pour voir les données</p>
                  </div>
                  
                  {/* Exemple de tableau avec période */}
                  <div className="bg-white rounded-lg border overflow-hidden">
                    <div className="px-6 py-3 bg-gray-50 border-b">
                      <h5 className="text-sm font-medium text-gray-900">
                        Aperçu du format de données - {(() => {
                          const reportData = selectedReport.data as Record<string, any> | undefined;
                          const period = reportData?.period;
                          if (period === 'day') return 'Aujourd\'hui';
                          if (period === 'week') return '7 derniers jours';
                          if (period === 'month') return 'Ce mois';
                          return 'Période';
                        })()}
                      </h5>
                    </div>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Indicateur
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Valeur
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr className="bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            Total Pesées
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            -
                          </td>
                        </tr>
                        <tr className="bg-white">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            Pesées Complétées
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            -
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            Pesées En Cours
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            -
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PredefinedReports;
